import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Input, Button, Card, Typography, message, Alert } from "antd";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabase";
import styled from "@emotion/styled";

const { Title } = Typography;

const ResetContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: #ffffff;
  padding: 20px;
`;

const StyledCard = styled(Card)`
  width: 100%;
  max-width: 400px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
`;

const Logo = styled.img`
  display: block;
  margin: 0 auto 24px;
  height: 60px;
  width: auto;
`;

export const ResetPassword = () => {
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form] = Form.useForm();

  useEffect(() => {
    // Handle password reset flow immediately when component mounts
    const handlePasswordReset = async () => {
      console.log(
        "🔄 ResetPassword: Component mounted, checking for reset tokens"
      );
      console.log("🔄 ResetPassword: Current URL:", window.location.href);
      console.log(
        "🔄 ResetPassword: Current pathname:",
        window.location.pathname
      );
      console.log("🔄 ResetPassword: Current search:", window.location.search);

      // Check if we have URL fragments (hash) that contain the reset tokens
      const hash = window.location.hash;
      console.log("🔍 ResetPassword: Current hash:", hash);

      if (
        hash &&
        hash.includes("access_token") &&
        hash.includes("type=recovery")
      ) {
        console.log("✅ ResetPassword: Found password reset tokens in URL");

        // Parse the hash to extract tokens
        const hashParams = new URLSearchParams(hash.substring(1));
        const accessToken = hashParams.get("access_token");
        const refreshToken = hashParams.get("refresh_token");
        const type = hashParams.get("type");

        if (accessToken && refreshToken && type === "recovery") {
          console.log("🔐 ResetPassword: Setting session with recovery tokens");

          try {
            const { data, error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });

            if (error) {
              console.error("❌ ResetPassword: Error setting session:", error);
              messageApi.error(t("auth.invalidResetLink"));
              setTimeout(() => {
                navigate("/auth");
              }, 2000);
              return;
            }

            if (data.session) {
              console.log("✅ ResetPassword: Session established successfully");
              // Clear the hash from the URL for security
              window.history.replaceState(null, "", window.location.pathname);
              return;
            }
          } catch (error) {
            console.error(
              "❌ ResetPassword: Exception during setSession:",
              error
            );
            messageApi.error(t("auth.invalidResetLink"));
            setTimeout(() => {
              navigate("/auth");
            }, 2000);
            return;
          }
        }
      }

      // If no hash tokens, check if we already have an active session
      console.log(
        "🔍 ResetPassword: No hash tokens found, checking for existing session"
      );
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        console.log("❌ ResetPassword: No session found, redirecting to auth");
        messageApi.error(t("auth.invalidResetLink"));
        setTimeout(() => {
          navigate("/auth");
        }, 2000);
      }
    };

    handlePasswordReset();
  }, [messageApi, navigate, t]);

  const handleResetPassword = async (values: { password: string }) => {
    try {
      setLoading(true);
      console.log("🔄 ResetPassword: Attempting to reset password");

      const { error } = await supabase.auth.updateUser({
        password: values.password,
      });

      if (error) {
        console.error("❌ ResetPassword: Error during password reset", error);
        throw error;
      }

      console.log("✅ ResetPassword: Password reset successful");
      messageApi.success(t("auth.passwordResetSuccess"));

      // Sign out to clear the reset session
      await supabase.auth.signOut();

      // Redirect to login page
      setTimeout(() => {
        navigate("/auth");
      }, 2000);
    } catch (error) {
      console.error("❌ ResetPassword: Exception during password reset", error);
      messageApi.error(
        error instanceof Error ? error.message : t("auth.passwordResetError")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <ResetContainer>
      {contextHolder}
      <StyledCard>
        <Logo src="/logo_black.png" alt="Logo" />
        <Title level={2} style={{ textAlign: "center", marginBottom: 24 }}>
          {t("auth.setNewPassword")}
        </Title>

        <Form form={form} onFinish={handleResetPassword} layout="vertical">
          <Form.Item
            name="password"
            label={t("auth.newPassword")}
            rules={[
              { required: true, message: t("auth.passwordRequired") },
              { min: 6, message: t("auth.passwordMinLength") },
            ]}
          >
            <Input.Password placeholder={t("auth.enterNewPassword")} />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label={t("auth.confirmPassword")}
            dependencies={["password"]}
            rules={[
              { required: true, message: t("auth.confirmPasswordRequired") },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(t("auth.passwordsDoNotMatch"))
                  );
                },
              }),
            ]}
          >
            <Input.Password placeholder={t("auth.confirmNewPassword")} />
          </Form.Item>

          <Button type="primary" htmlType="submit" block loading={loading}>
            {t("auth.resetPassword")}
          </Button>
        </Form>
      </StyledCard>
    </ResetContainer>
  );
};
